/**
 * usePeerConnections - Polls the live RTCPeerConnections behind the current calls
 *
 * The peer connections live in WebRTCService, not in the store, and their state
 * machines move without any React-visible event. Polling once a second is what
 * makes the panel track a connection as it renegotiates or fails.
 */

import type { PeerConnectionInfo } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/WebRTCTab/index.types';
import { extractIceCandidatesFromSdp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

export const PEER_CONNECTION_POLL_INTERVAL_MS = 1000;

const toInfo = (callId: string, pc: RTCPeerConnection): PeerConnectionInfo => {
  const localSdp = pc.localDescription?.sdp || null;
  const remoteSdp = pc.remoteDescription?.sdp || null;

  return {
    callId,
    connectionState: pc.connectionState,
    iceConnectionState: pc.iceConnectionState,
    signalingState: pc.signalingState,
    localSdpType: pc.localDescription?.type || null,
    remoteSdpType: pc.remoteDescription?.type || null,
    localCandidatesCount: extractIceCandidatesFromSdp(localSdp),
    remoteCandidatesCount: extractIceCandidatesFromSdp(remoteSdp),
    localSdp,
    remoteSdp,
  };
};

export const usePeerConnections = (): PeerConnectionInfo[] => {
  const { activeCalls, activeConference, warmTransferContext } =
    useSoftphoneStore(
      useShallow((state) => ({
        activeCalls: state.activeCalls,
        activeConference: state.activeConference,
        warmTransferContext: state.warmTransferContext,
      }))
    );
  const [peerConnections, setPeerConnections] = useState<PeerConnectionInfo[]>(
    []
  );

  useEffect(() => {
    const updatePeerConnections = () => {
      const webrtcService = WebRTCService.getInstance();
      const infos: PeerConnectionInfo[] = [];

      activeCalls.forEach((call) => {
        const pc = webrtcService.getPeerConnection(call.id);
        if (!pc) {
          return;
        }

        // During a warm transfer two connections are up at once. Without a
        // role prefix they are two anonymous ids, and telling which one is
        // the consultation is where one-way-audio diagnosis starts.
        const role =
          warmTransferContext?.consultationCallId === call.id
            ? 'consultation'
            : warmTransferContext?.originalCallId === call.id
              ? 'original'
              : null;

        infos.push(toInfo(role ? `${role}:${call.id}` : call.id, pc));
      });

      // Also include the conference room PeerConnection (keyed by roomGuid)
      if (activeConference?.roomGuid) {
        const pc = webrtcService.getPeerConnection(activeConference.roomGuid);
        if (pc) {
          infos.push(toInfo(`conference:${activeConference.roomGuid}`, pc));
        }
      }

      setPeerConnections(infos);
    };

    updatePeerConnections();

    const interval = setInterval(
      updatePeerConnections,
      PEER_CONNECTION_POLL_INTERVAL_MS
    );

    return () => clearInterval(interval);
  }, [activeCalls, activeConference, warmTransferContext]);

  return peerConnections;
};
